import { createFileRoute } from "@tanstack/react-router";
import AnswerPage from "@/pages/AnswerPage";
import { getAnswerPreviewSsr } from "@/lib/answerPreviewSsr.functions";
import {
  answerDescriptionFromBody,
  answerHead,
  answerPath,
  answerSchemaText,
  answersBase,
  resolveAnswerParams,
  sectorLabel,
} from "@/lib/answersSeo";
import { questionText } from "@/data/questionBank";
import { SITE_URL } from "@/lib/site-url";

export const Route = createFileRoute("/en/answers/$sector/$question")({
  loader: async ({ params }) => { try { return await getAnswerPreviewSsr({ data: { sector: params.sector, question: params.question, lang: "en" } }); } catch { return { content: null }; } },
  head: ({ params, loaderData }) => {
    const resolved = resolveAnswerParams(params.sector, params.question, "en");
    const body = loaderData?.content?.body ?? null;
    const base = resolved
      ? answerHead("en", sectorLabel(resolved.sector, "en"), questionText(resolved.question, "en", resolved.sector.key), resolved.question.category)
      : { title: "Answers by business type | Retail Lemon", description: "Answers by type of business." };
    const h = {
      title: base.title,
      description: resolved
        ? answerDescriptionFromBody(body ?? loaderData?.content?.metaDescription, base.description)
        : base.description,
    };
    const pathEs = resolved ? answerPath("es", resolved.sector, resolved.question) : "/consultas-resueltas";
    const pathEn = resolved ? answerPath("en", resolved.sector, resolved.question) : "/en/answers";
    const questionLabel = resolved ? questionText(resolved.question, "en", resolved.sector.key) : "";
    return {
      meta: [
        { title: h.title },
        { name: "description", content: h.description },
        { property: "og:title", content: h.title },
        { property: "og:description", content: h.description },
        { property: "og:url", content: `${SITE_URL}${pathEn}` },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: h.title },
        { name: "twitter:description", content: h.description },
        ...(resolved ? [] : [{ name: "robots", content: "noindex" }]),
      ],
      links: resolved ? [
        { rel: "canonical", href: `${SITE_URL}${pathEn}` },
        { rel: "alternate", hrefLang: "es", href: `${SITE_URL}${pathEs}` },
        { rel: "alternate", hrefLang: "en", href: `${SITE_URL}${pathEn}` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${pathEs}` },
      ] : [],
      scripts: resolved ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "QAPage",
            inLanguage: "en",
            url: `${SITE_URL}${pathEn}`,
            about: sectorLabel(resolved.sector, "en"),
            mainEntity: {
              "@type": "Question",
              name: questionLabel,
              answerCount: 1,
              acceptedAnswer: {
                "@type": "Answer",
                text: answerSchemaText(body, h.description),
                url: `${SITE_URL}${pathEn}`,
              },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Retail Lemon", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Answers", item: `${SITE_URL}${answersBase("en")}` },
              { "@type": "ListItem", position: 3, name: sectorLabel(resolved.sector, "en"), item: `${SITE_URL}${answersBase("en")}?sector=${resolved.sector.key}` },
              { "@type": "ListItem", position: 4, name: questionLabel, item: `${SITE_URL}${pathEn}` },
            ],
          }),
        },
      ] : [],
    };
  },
  component: AnswerRoute,
});

function AnswerRoute() {
  const data = Route.useLoaderData();
  return <AnswerPage routeLang="en" initialContent={data.content} />;
}
